import { useUser } from "@clerk/clerk-react";
import { ArrowUpRight, Menu, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminScope } from "@/contexts/AdminScopeContext";
import { useMobileSidebar } from "@/contexts/MobileSidebarContext";
import { isSuperadminRole } from "@/lib/roles";
import type { AdminScopeKind } from "./adminNav";

interface AdminHeaderProps {
  /** Current page name, shown on the left. Derived from the route by AdminLayout. */
  title: string;
  /** Scope controls declared by the current route (adminNav); undefined = none. */
  scope?: AdminScopeKind;
}

// Radix Select forbids empty-string values, so "all" is a sentinel.
const ALL = "ALL";

/** Cohort lifecycle, as computed from its start/end dates. Every cohort the
 * switcher lists gets tagged — the list spans drafts through completed, so an
 * untagged name is ambiguous. Archived cohorts never reach here (KC-387). */
const STATUS_LABEL: Record<string, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};

/** Cohort switcher backed by the shared admin scope. Hidden for assessors
 * (they cannot list cohorts). "All cohorts" is a real choice — pages whose
 * queries need a concrete cohort fall back to the default. */
function CohortSwitcher() {
  const { programmeId, programmes, canSwitchCohort, setProgrammeId } = useAdminScope();
  if (!canSwitchCohort) return null;

  const selected = programmes.find((p) => p.id === programmeId);

  return (
    <Select value={programmeId ?? ALL} onValueChange={(v) => setProgrammeId(v === ALL ? null : v)}>
      {/* The `[&>span]` overrides target Radix's SelectValue span: it defaults to
          line-clamp-1, which lets a long cohort name wrap out of the fixed-height
          trigger instead of ellipsising. Making it a min-w-0 flex row lets the
          name truncate while the badge keeps its width. */}
      <SelectTrigger className="w-56 border-green-800/20 bg-green-50/60 font-medium text-green-900 [&>span]:line-clamp-none [&>span]:flex [&>span]:min-w-0 [&>span]:flex-1 [&>span]:items-center [&>span]:gap-2">
        {/* Explicit children, not the default clone of the selected item: the
            trigger has no room for the list's full tag set, and cloning it is
            what overflowed. The status label stays in the list; Draft is worth
            surfacing here because being scoped to an unpublished cohort is the
            surprising state. */}
        <SelectValue>
          <span className="truncate">{selected?.name ?? "All cohorts"}</span>
          {selected?.is_draft && (
            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
              Draft
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-w-[22rem]">
        {programmes.map((p) => (
          // `[&>span:last-child]` is Radix's ItemText wrapper. It sits between
          // the item's flex row and our content without min-w-0, which breaks
          // the shrink chain and lets a long name blow past the panel edge and
          // get hard-clipped instead of ellipsising.
          <SelectItem
            key={p.id}
            value={p.id}
            className="[&>span:last-child]:min-w-0 [&>span:last-child]:flex-1"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{p.name}</span>
              {/* Draft-ness and status are independent axes — a draft cohort
                  still has a lifecycle, so both tags can show at once. Tags
                  never shrink; the name yields first. */}
              {p.is_draft && (
                <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                  Draft
                </span>
              )}
              <span className="shrink-0 text-xs text-gray-500">
                {STATUS_LABEL[p.status] ?? p.status}
              </span>
            </span>
          </SelectItem>
        ))}
        <SelectItem value={ALL}>All cohorts</SelectItem>
      </SelectContent>
    </Select>
  );
}

/** W#A# slot select. Options follow the selected cohort's length; with "All
 * cohorts" selected they cover the longest cohort. */
function AssignmentSlotSelect() {
  const { programmeId, week, assignment, programmes, setSlot } = useAdminScope();

  const selected = programmes.find((p) => p.id === programmeId);
  const totalWeeks =
    selected?.total_weeks ?? Math.max(4, ...programmes.map((p) => p.total_weeks || 0));
  const weeks = Array.from({ length: totalWeeks }, (_, i) => i + 1);

  const value = week !== null && assignment !== null ? `${week}-${assignment}` : ALL;

  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (v === ALL) {
          setSlot(null, null);
        } else {
          const [w, a] = v.split("-").map(Number);
          setSlot(w, a);
        }
      }}
    >
      <SelectTrigger className="w-36">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All assignments</SelectItem>
        {weeks.flatMap((w) =>
          [1, 2].map((a) => (
            <SelectItem key={`${w}-${a}`} value={`${w}-${a}`}>
              W{w}A{a}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

/**
 * Admin-only top bar: a light header that sits to the right of the dark sidebar.
 * Left = mobile sidebar trigger + current page name. Right = the shared
 * cohort/assignment scope controls (only on routes that declare a `scope` in
 * adminNav) + a small "To app" button back to the participant experience.
 * Otherwise deliberately minimal — no global search, notifications, or avatar
 * (identity/logout live in the sidebar footer).
 *
 * Note: this intentionally does NOT reuse the shared <TopBar>, which is still
 * used by the participant side; restyling is scoped to admin only.
 */
export function AdminHeader({ title, scope }: AdminHeaderProps) {
  const { toggle } = useMobileSidebar();
  const { user } = useUser();
  const isSuperadmin = isSuperadminRole(user?.publicMetadata?.role as string | undefined);

  return (
    <header className="flex h-14 flex-shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 md:px-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 md:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-4 w-4" />
        </button>
        <h1 className="text-sm font-semibold text-gray-900">{title}</h1>
        {/* Elevated-access indicator: makes it explicit the user is operating as
         * superadmin (who can edit locked cohorts, enrol past cutoff, etc.). */}
        {isSuperadmin && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800"
            title="You are operating as superadmin"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Superadmin
          </span>
        )}
      </div>

      <div className="flex min-w-0 items-center gap-2">
        {scope && <CohortSwitcher />}
        {scope === "cohort+assignment" && <AssignmentSlotSelect />}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          To app
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </header>
  );
}
